if (!customElements.get('wlsn-add-to-cart')) {
  class WLSNAddToCart extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        add: 'wlsn-add-to-cart__add',
        incart: 'wlsn-add-to-cart__incart',
        quantitySelector: 'wlsn-quantity-selector',
      };

      this.purchaseType = {
        OTP: 'one-time',
        SUB: 'subscription',
      };

      this.lineKey = null;
      this._isUpdatingCart = false;

      this.boundRefresh = this._refresh.bind(this);
      this.boundOnCartApiSuccess = this._onCartApiSuccess.bind(this);
      this.boundOnPageUpdated = this._refresh.bind(this);
      this.boundOnPurchaseTypeChange = this._refresh.bind(this);
      this.boundOnQuantityChange = this._onQuantityChange.bind(this);
      this.boundOnVariantMutations = this._onVariantMutations.bind(this);
    }

    get elements() {
      return {
        addWrap: this.querySelector(`.${this.classes.add}`),
        incartWrap: this.querySelector(`.${this.classes.incart}`),
        quantitySelector: this.querySelector(this.classes.quantitySelector),
      };
    }

    connectedCallback() {
      this._cacheFormRefs();
      this._bind();
      this._observeVariantInput();
      this._refresh();
    }

    disconnectedCallback() {
      this._unbind();
      this._disconnectVariantObserver();
      this._boundForm = null;
      this._variantInput = null;
    }

    _cacheFormRefs() {
      const pf = this._getProductForm();
      this._boundForm = pf?.querySelector('form') || null;
      this._variantInput = this._boundForm?.querySelector('input[name="id"]') || null;
    }

    _getProductForm() {
      return this.closest('wlsn-product-form');
    }

    _getForm() {
      if (this._boundForm && document.contains(this._boundForm)) {
        return this._boundForm;
      }
      this._cacheFormRefs();
      return this._boundForm;
    }

    /**
     * @returns {{ variantId: string, purchaseType: string, sellingPlan: string }|null}
     */
    _readFormSelection() {
      const form = this._getForm();
      if (!form) return null;

      const variantInput = form.querySelector('input[name="id"]');
      const variantId = variantInput?.value?.trim() || '';
      if (!variantId) return null;

      const ptInput = form.querySelector('input[name="purchase-type"]:checked');
      const purchaseType = ptInput ? ptInput.value : this.purchaseType.OTP;

      let sellingPlan = '';
      if (purchaseType === this.purchaseType.SUB) {
        const freq = form.querySelector('wlsn-product-form-frequency');
        const planInput =
          freq?.querySelector('input[name="selling_plan"]') || form.querySelector('input[name="selling_plan"]');
        sellingPlan = planInput?.value?.trim() || '';
      }

      return { variantId, purchaseType, sellingPlan };
    }

    /**
     * @param {Array<object>} items - cart.items
     * @returns {object|undefined}
     */
    _findMatchingLine(items, variantId, purchaseType, sellingPlan) {
      const vid = Number(variantId);
      if (Number.isNaN(vid)) return undefined;

      return items.find((item) => {
        if (Number(item.variant_id) !== vid) return false;

        const planId = item.selling_plan_allocation?.selling_plan?.id;
        const hasPlan = planId != null;

        if (purchaseType === this.purchaseType.SUB && sellingPlan) {
          return hasPlan && String(planId) === String(sellingPlan);
        }

        return !hasPlan;
      });
    }

    _setMode(inCart, quantity) {
      const { addWrap, incartWrap, quantitySelector } = this.elements;
      if (!addWrap || !incartWrap) return;

      if (inCart && quantity > 0) {
        addWrap.hidden = true;
        addWrap.setAttribute('aria-hidden', 'true');
        incartWrap.hidden = false;
        incartWrap.setAttribute('aria-hidden', 'false');

        if (quantitySelector && typeof quantitySelector.setQuantity === 'function') {
          quantitySelector.setQuantity(quantity);
        }
      } else {
        addWrap.hidden = false;
        addWrap.setAttribute('aria-hidden', 'false');
        incartWrap.hidden = true;
        incartWrap.setAttribute('aria-hidden', 'true');
        this.lineKey = null;
      }
    }

    _setStepperLoading(loading) {
      const { quantitySelector } = this.elements;
      if (quantitySelector && typeof quantitySelector.setLoadingState === 'function') {
        quantitySelector.setLoadingState(loading);
      }
    }

    async _fetchCartJson() {
      const cartUrl = window.routes?.cart_url;
      if (!cartUrl) return null;
      const url = cartUrl.endsWith('.js') ? cartUrl : `${cartUrl.replace(/\/$/, '')}.js`;
      const res = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return res.json();
    }

    async _refresh() {
      const selection = this._readFormSelection();
      if (!selection) {
        this._setMode(false, 0);
        return;
      }

      const cart = await this._fetchCartJson();
      if (!cart?.items) {
        this._setMode(false, 0);
        return;
      }

      const line = this._findMatchingLine(
        cart.items,
        selection.variantId,
        selection.purchaseType,
        selection.sellingPlan
      );

      if (line && line.quantity > 0) {
        this.lineKey = line.key;
        this._setMode(true, line.quantity);
      } else {
        this._setMode(false, 0);
      }
    }

    _hydrateFromCart(cart) {
      const selection = this._readFormSelection();
      if (!selection || !cart?.items) {
        this._setMode(false, 0);
        return;
      }

      const line = this._findMatchingLine(
        cart.items,
        selection.variantId,
        selection.purchaseType,
        selection.sellingPlan
      );

      if (line && line.quantity > 0) {
        this.lineKey = line.key;
        this._setMode(true, line.quantity);
      } else {
        this._setMode(false, 0);
      }
    }

    _onCartApiSuccess(event) {
      const data = event.detail?.data;
      if (data?.items) {
        this._hydrateFromCart(data);
      } else if (event.detail?.kind === window.WLSN_CART?.API_URLS?.ADD) {
        this._refresh();
      }
    }

    async _changeLineQuantity(lineKey, quantity) {
      const changeUrl = window.routes?.cart_change_url;
      if (!changeUrl || !lineKey) return;

      this._isUpdatingCart = true;
      this._setStepperLoading(true);

      try {
        const response = await fetch(`${changeUrl}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: lineKey,
            quantity,
            sections: ['wlsn-cart-icon-buble'],
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          document.dispatchEvent(
            new CustomEvent('wlsn:cart:update:error', {
              detail: { lineKey, error: data.message || data.description || 'Cart update failed' },
            })
          );
          await this._refresh();
          return;
        }

        this._hydrateFromCart(data);
      } catch (err) {
        console.error('[WLSNAddToCart] Cart change error:', err);
        document.dispatchEvent(
          new CustomEvent('wlsn:cart:update:error', {
            detail: { lineKey, error: err.message },
          })
        );
        await this._refresh();
      } finally {
        this._setStepperLoading(false);
        this._isUpdatingCart = false;
      }
    }

    _onQuantityChange(event) {
      if (this._isUpdatingCart) return;

      const { quantity } = event.detail || {};
      if (quantity === undefined || quantity === null) return;

      const key = this.lineKey;
      if (!key) {
        this._refresh();
        return;
      }

      this._changeLineQuantity(key, Number(quantity));
    }

    _bind() {
      const { quantitySelector } = this.elements;
      if (quantitySelector) {
        quantitySelector.addEventListener('quantity:change', this.boundOnQuantityChange);
      }

      document.addEventListener('wlsn:cart:api:success', this.boundOnCartApiSuccess);
      window.addEventListener('wlsn:page:updated', this.boundOnPageUpdated);
      window.addEventListener('wlsn:purchase-type:change', this.boundOnPurchaseTypeChange);

      if (this._boundForm) {
        this._boundForm.addEventListener('change', this.boundRefresh);
      }
    }

    _unbind() {
      const { quantitySelector } = this.elements;
      if (quantitySelector) {
        quantitySelector.removeEventListener('quantity:change', this.boundOnQuantityChange);
      }

      document.removeEventListener('wlsn:cart:api:success', this.boundOnCartApiSuccess);
      window.removeEventListener('wlsn:page:updated', this.boundOnPageUpdated);
      window.removeEventListener('wlsn:purchase-type:change', this.boundOnPurchaseTypeChange);

      if (this._boundForm) {
        this._boundForm.removeEventListener('change', this.boundRefresh);
      }
    }

    _observeVariantInput() {
      if (!this._variantInput) return;

      this._variantObserver = new MutationObserver(this.boundOnVariantMutations);
      this._variantObserver.observe(this._variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    _disconnectVariantObserver() {
      if (this._variantObserver) {
        this._variantObserver.disconnect();
        this._variantObserver = null;
      }
    }

    _onVariantMutations() {
      this._refresh();
    }
  }

  customElements.define('wlsn-add-to-cart', WLSNAddToCart);
}
